import { Component, OnInit,ViewEncapsulation } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { DomSanitizer } from '@angular/platform-browser';
// Models
import { Vehicle, VehicleLogbook, VehicleLogTableItem } from '@mocca-technologies/model';
// Services
import { LogbooksSharedService } from '../../../../../services/logbooks-shared.service';
// Components
import { DocumentVisualizerComponent } from 'src/app/components/document-visualizer/document-visualizer.component';
// PDFMake
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { customFonts } from '../../../../../../assets/fonts/customFonts'

@Component({
  selector: 'app-drone-logbook-template',
  templateUrl: './drone-logbook-template.component.html',
  styleUrls: ['./drone-logbook-template.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class DroneLogbookTemplate implements OnInit {

  pdfDocument: Blob;
  pdfUrl: string;
  rows: VehicleLogTableItem[];
  droneLogbook: VehicleLogbook;
  selectedDrone: Vehicle;

  droneHighchart: string;

  constructor(private modalCtrl: ModalController,
    private loadingControl: LoadingController,
    private sharedService: LogbooksSharedService,
    private sanitizer: DomSanitizer,
  ) {
    this.generatePDF(2);
  }

  ngOnInit() {

    this.loadCustomFonts();

    this.sharedService.svg$.subscribe(svg => {
      this.droneHighchart = svg;
    });

  }

  /**
 * This method is used to load all the custom fonts in the customFonts.js file in the vfs of pdfmake
 */
  loadCustomFonts(){

    pdfFonts.pdfMake.vfs['Calibri-Normal']=customFonts.Calibri.normal;
    pdfFonts.pdfMake.vfs['Calibri-Bold']=customFonts.Calibri.bold;
    pdfFonts.pdfMake.vfs['Calibri-Italics']=customFonts.Calibri.italics;
    pdfFonts.pdfMake.vfs['Calibri-BoldItalics']=customFonts.Calibri.bolditalics;
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    pdfMake.fonts = {
      Calibri: {
        normal: "Calibri-Normal",
        bold: "Calibri-Bold",
        italics: "Calibri-Italics",
        bolditalics: "Calibri-BoldItalics",
      },
    }
  }

  close(){
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  /**
   * This method is used to build and generate the pdf.
   * @param action : it can be 1, if we want to download the pdf, or 2 if we just want to show it.
   */
  async generatePDF(action: number) {
    const loading = await this.loadingControl.create({
      message: 'Generating document...',
      spinner: 'bubbles',
    });
    await loading.present();

    // #region --> Doc Definition ---------------------------------------------------
    loading.message = 'Defining document...';

    let docDefinition = {
      content: [],
      pageBreakBefore: function(currentNode, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage) {
        return currentNode.headlineLevel === 1 && followingNodesOnPage.length === 0;
      },
      pageMargins: [40, 60, 40, 60],
      pageSize: 'A4',
      pageOrientation: 'landscape',
      styles: {
        header0: {
          fontSize: 32,
          bold: true,
        },
        header1: {
          fontSize: 24,
          bold: true,
        },
        header2: {
          fontSize: 13,
          bold: true,
        },
        header3: {
          fontSize: 16,
          bold: true,
        },
        header4: {
          fontSize: 12,
          bold: true,
          italics: true,
        },
        header5: {
          fontSize: 10.72,
          bold: true,
        },
      },
      defaultStyle: {
        font: 'Calibri',
        fontSize: 12,
        lineHeight: 1.5,
      }
    };

    // #endregion --> Doc Definition ---------------------------------------------------

    // #region --> Loading Summary Table ---------------------------------------------------

    loading.message = 'Loading summary table...';

    let titleMessage=[
      {text: "Drone Logbook - " + this.selectedDrone?.plate + " - " + this.selectedDrone?.manufacturer + " " + this.selectedDrone?.model, alignment: 'center', bold: true, lineHeight: 1.5, style: 'header1'},
      {text: " "},
    ];
    docDefinition.content.push(titleMessage);

    let logTable = {
      layout: 'lightHorizontalLines',
      table: {
        headerRows: 1,
        widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
        body: [
          ['Date', 'Location', 'Take Off Time', 'Landing Time', 'Pilot', 'Flight Duration', 'Number of Flights', 'Activity', 'Battery Consumption', 'Notes']
        ],
      },
    }

    this.rows.forEach(row => {
      logTable.table.body.push([
        row?.date,
        row?.location,
        row?.takeoffTime,
        row?.landingTime,
        row?.pilot,
        row?.flightDuration?.toString(),
        row?.numFlights?.toString(),
        row?.activity ? row.activity : '',
        row?.batteryConsumption ? row.batteryConsumption : '',
        row?.notes ? row.notes : '',
      ]);
    });

    logTable.table.body.push([
      '',
      '',
      '',
      '',
      '',
      (this.droneLogbook?.totalFlightTime/60000).toFixed(3).toString(),
      this.getTotalFlights().toString(),
      '',
      '',
      ''
    ]);

    docDefinition.content.push(logTable);

    loading.message = 'Generating PDF...';
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      await new Promise<Blob>((resolve, reject) => {
        pdfDocGenerator.getBlob((blob) => {
          this.pdfDocument = blob;
          resolve(blob);
        });
      }).then(async () => {
        const url = window.URL.createObjectURL(this.pdfDocument);
        let documentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        const modal = await this.modalCtrl.create({
          component: DocumentVisualizerComponent,
          cssClass: "documentVisualizer",
          componentProps:{
            url: url,
            filename: "DroneLogbookTest-"+this.selectedDrone?.plate+"-"+this.selectedDrone?.manufacturer+"-"+this.selectedDrone?.model+".pdf",
            documentUrl: documentUrl,
          }
        });
        this.modalCtrl.dismiss();
        loading.dismiss()
        modal.present();

        await modal.onWillDismiss();

      });
    loading.dismiss();
  }

  getTotalFlights(): number {
    return this.rows.reduce((total, row) => total + (row.numFlights || 0), 0);
  }

}
